<template>
	<div class="mx-xs md:mx-md lg:mx-lg">
		<v-breadcrumbs class="mx-sm mt-sm mb-md sm:container sm:mx-auto" />

		<v-storyblok-story v-if="story" :story="story" />
	</div>
</template>

<script setup lang="ts">
import type {ISbStoryData} from "storyblok-js-client";

import {useStoryblokStory} from "@pkg/shared/composables/use-storyblok-story";

// app.vue
const isForegroundRequest: boolean = inject("isForegroundRequest", true);

const route = useRoute();

const fullSlug = route.path === "/" || route.path === "" ? "home" : route.path;

const story = ref<ISbStoryData>();

try {
	const {
		data: {value: storyData},
	} = await useStoryblokStory(fullSlug, {
		resolve_relations: [
			"sb-brands-listing.content",
			"sb-brands-listing-big.content",
		].join(","),
	});

	if (!storyData) {
		throw new Error("Page Not Found", {cause: "story-not-found"});
	}

	story.value = storyData;
} catch {
	// Don't create a nuxt error for missing story if this isn't the actual client trying to navigate to a page but rather a background request into an invalid path caused by some issue with the page or its scripts (see `app.vue`), instead we'll just render an empty page. Throwing a nuxt error here could cause the client's active app to go to the error page even if the error was thrown from a server-rendering background request (which is really weird and may not happen always - another nuxt bug?).
	if (isForegroundRequest) {
		throw createError({statusCode: 404, statusMessage: "Page Not Found"});
	}
}

useSeoMeta({
	title: story.value?.content.meta?.title || story.value?.name,
	ogTitle: story.value?.content.meta?.title || story.value?.name,
	description: story.value?.content.meta?.description ?? undefined,
	ogDescription:
		story.value?.content.meta?.description ??
		story.value?.content?.short_description ??
		undefined,
});
</script>
